import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import './landscape-movie-list.scss';

const fmtDuration = (min) => {
    if (!min) return null;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
};

const getStatusLabel = (item) => {
    const s = (item.status || '').toLowerCase();
    if (s.includes('sap') || s.includes('soon') || s.includes('upcoming')) return 'soon';
    if (item.quality === 'CAM' || s.includes('cam')) return 'cam';
    return null;
};

const LandscapeMovieList = ({ genreId, type, size = 12 }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const swiperRef = useRef(null);

    useEffect(() => {
        let url;
        if (genreId) {
            url = `/movies-public-api.php?type=byGenre&genreId=${genreId}&page=0&size=${size}`;
        } else if (type === 'popular') {
            url = `/movies-public-api.php?type=popular&page=0&size=${size}`;
        } else if (type === 'toprated') {
            url = `/movies-public-api.php?type=top-rated&page=0&size=${size}`;
        } else if (type === 'new') {
            url = `/movies-public-api.php?type=similar&page=0&size=${size}`;
        } else return;

        setLoading(true);
        fetch(url)
            .then(r => r.json())
            .then(d => { if (d.success) setItems(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [genreId, type, size]);

    const updateNav = s => { setIsBeginning(s.isBeginning); setIsEnd(s.isEnd); };

    if (loading) return <div className="lml-loading">Đang tải...</div>;
    if (!items.length) return null;

    return (
        <div className="lml-wrap">
            {!isBeginning && (
                <button className="lml-nav lml-nav--prev" onClick={() => {
                    const s = swiperRef.current;
                    if (!s) return;
                    const step = s.slidesPerViewDynamic?.() || 1;
                    s.slideTo(Math.max(0, s.activeIndex - step));
                }}>
                    <FaChevronLeft />
                </button>
            )}
            {!isEnd && (
                <button className="lml-nav lml-nav--next" onClick={() => {
                    const s = swiperRef.current;
                    if (!s) return;
                    const step = s.slidesPerViewDynamic?.() || 1;
                    s.slideTo(s.activeIndex + step);
                }}>
                    <FaChevronRight />
                </button>
            )}
            <Swiper
                grabCursor
                spaceBetween={14}
                slidesPerView="auto"
                onSwiper={s => { swiperRef.current = s; updateNav(s); }}
                onSlideChange={updateNav}
                onActiveIndexChange={updateNav}
                onReachBeginning={updateNav}
                onReachEnd={updateNav}
                onResize={updateNav}
            >
                {items.map(item => {
                    const banner   = item.thumbnailUrl || item.thumbUrl || item.backdropUrl || item.posterUrl || item.imageUrl;
                    const poster   = item.posterUrl || item.imageUrl || banner;
                    const watchUrl = `/watch/${item.slug}/${encryptId(1)}`;
                    const year     = item.releaseYear || item.year || '';
                    const dur      = fmtDuration(item.duration);
                    const rating   = item.ageRating || item.rated || null;
                    const statusKey = getStatusLabel(item);
                    const eps = item.episodeCount > 1 ? `${item.episodeCount} tập` : null;
                    const score = item.averageRating > 0 ? Number(item.averageRating).toFixed(1) : null;

                    return (
                        <SwiperSlide key={item.id} className="lml-slide">
                            <Link to={watchUrl} className="lml-card">
                                {/* ── Banner 16:9 ── */}
                                <div className="lml-card__banner">
                                    <img src={banner} alt={item.title} loading="lazy" decoding="async"
                                        onError={e => { if (e.target.src !== poster) e.target.src = poster; }} />
                                    <div className="lml-card__overlay" />
                                </div>

                                {/* ── Footer: poster chổng + info ngoài ── */}
                                <div className="lml-card__footer">
                                    <div className="lml-card__poster">
                                        <img src={poster} alt={item.title} loading="lazy" decoding="async"
                                            onError={e => { e.target.style.display = 'none'; }} />
                                        {statusKey === 'soon' && (
                                            <span className="lml-card__status lml-card__status--soon">Sắp chiếu</span>
                                        )}
                                        {!statusKey && item.quality && item.quality !== 'HD' && (
                                            <span className="lml-card__status lml-card__status--hd">{item.quality}</span>
                                        )}
                                    </div>

                                    <div className="lml-card__info">
                                        <p className="lml-card__title">{item.title}</p>
                                        {item.originalTitle && item.originalTitle !== item.title && (
                                            <p className="lml-card__subtitle">{item.originalTitle}</p>
                                        )}
                                        <div className="lml-card__meta">
                                            {rating && <span className="lml-rating">{rating}</span>}
                                            {year && <span>{year}</span>}
                                            {dur && <><span className="lml-dot" /><span>{dur}</span></>}
                                            {eps && <><span className="lml-dot" /><span>{eps}</span></>}
                                            {score && <><span className="lml-dot" /><span className="lml-score">★ {score}</span></>}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};

export default LandscapeMovieList;
